import { useQuery } from '@tanstack/react-query';
import {
  Users,
  FileText,
  TrendingUp,
  AlertTriangle,
  BookOpen,
  GraduationCap,
  UserPlus,
  Calendar,
  Activity,
  CheckCircle,
  Clock,
  ShieldCheck,
  ArrowRight,
  BarChart3,
} from 'lucide-react';
import { StatCard, CircularProgress, Skeleton, Card, CardHeader, CardFooter } from '@azyntor-labs/nexus-ui';
import { dashboardApi } from '@/api/endpoints';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

// Loading skeleton for admin dashboard
const AdminDashboardSkeleton = () => (
  <div className="space-y-6 animate-in fade-in duration-300">
    {/* Header skeleton */}
    <div className="flex items-center justify-between">
      <div>
        <Skeleton width={200} height={28} className="rounded-lg" />
        <Skeleton width={280} height={16} className="mt-2 rounded" />
      </div>
      <Skeleton width={120} height={36} className="rounded-lg" />
    </div>

    {/* Main Stats Grid skeleton - 6 columns */}
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="glass-card p-4">
          <div className="flex items-center justify-between mb-3">
            <Skeleton width={80} height={12} className="rounded" />
            <Skeleton variant="circular" width={32} height={32} />
          </div>
          <Skeleton width={60} height={24} className="rounded-lg" />
          <Skeleton width={100} height={8} className="mt-2 rounded-full" />
        </div>
      ))}
    </div>

    {/* Secondary Stats Row skeleton */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="stat-card">
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <Skeleton width={70} height={10} className="rounded" />
              <Skeleton width={50} height={20} className="rounded-lg" />
            </div>
            <Skeleton variant="rounded" width={32} height={32} />
          </div>
        </div>
      ))}
    </div>

    {/* Cards Grid skeleton */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="glass-card p-5">
          <div className="flex items-center justify-between mb-4">
            <Skeleton width={120} height={18} className="rounded-lg" />
            <Skeleton width={60} height={24} className="rounded-full" />
          </div>
          <div className="space-y-3">
            {[1, 2, 3].map((j) => (
              <div key={j} className="flex items-center space-x-3">
                <Skeleton width="100%" height={12} className="rounded" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Compact User Distribution Component
const UserDistribution = ({ userStats }: { userStats: any }) => {
  const total = userStats?.total_users || 1;
  const distributions = [
    { label: 'Students', value: userStats?.total_students || 0, color: 'bg-teal-500' },
    { label: 'Teachers', value: userStats?.total_teachers || 0, color: 'bg-emerald-500' },
    { label: 'Admins', value: userStats?.total_admins || 0, color: 'bg-purple-500' },
  ];

  return (
    <div className="space-y-3">
      {distributions.map((item) => {
        const percentage = total > 0 ? ((item.value / total) * 100).toFixed(1) : 0;
        return (
          <div key={item.label} className="flex items-center space-x-3">
            <div className="w-20 text-xs text-white/50">{item.label}</div>
            <div className="flex-1">
              <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                <div
                  className={`h-full rounded-full ${item.color}`}
                  style={{ width: `${percentage}%` }}
                />
              </div>
            </div>
            <div className="w-20 text-right">
              <span className="text-xs font-semibold text-white/70">{item.value}</span>
              <span className="text-xs text-white/40 ml-1">({percentage}%)</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Exam Status Distribution
const ExamStatusDistribution = ({ examStats }: { examStats: any }) => {
  const statuses = [
    { label: 'Draft', value: examStats?.draft_exams || 0, color: 'text-white/40', icon: FileText },
    { label: 'Scheduled', value: examStats?.scheduled_exams || 0, color: 'text-amber-400', icon: Calendar },
    { label: 'Active', value: examStats?.active_exams || 0, color: 'text-emerald-400', icon: Activity },
    { label: 'Completed', value: examStats?.completed_exams || 0, color: 'text-teal-400', icon: CheckCircle },
  ];

  return (
    <div className="grid grid-cols-2 gap-3">
      {statuses.map((status) => {
        const Icon = status.icon;
        return (
          <div
            key={status.label}
            className="flex items-center space-x-2 p-2.5 rounded-lg bg-white/[0.02] border border-white/[0.04]"
          >
            <Icon className={`w-4 h-4 ${status.color}`} />
            <div>
              <p className="text-lg font-semibold text-white/90">{status.value}</p>
              <p className="text-[10px] text-white/40 uppercase tracking-wider">{status.label}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const AdminDashboard = () => {
  const { data: dashboardResponse, isLoading: isLoadingDashboard, error: dashboardError } = useQuery({
    queryKey: ['admin-dashboard'],
    queryFn: () => dashboardApi.getAdminDashboard(),
  });

  const dashboard = dashboardResponse?.data?.data;
  const userStats = dashboard?.user_stats;
  const examStats = dashboard?.exam_stats;
  const recentActivity = dashboard?.recent_activity || [];

  if (isLoadingDashboard) {
    return <AdminDashboardSkeleton />;
  }

  if (dashboardError) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-white/90">Admin Dashboard</h1>
          <p className="text-white/50 mt-1 text-sm">Welcome back! Here's what's happening today.</p>
        </div>
        <Card>
          <div className="p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-6 h-6 text-amber-400" />
            </div>
            <h3 className="text-base font-semibold text-white/90 mb-2">Unable to load dashboard</h3>
            <p className="text-sm text-white/50 mb-4">We're having trouble loading your dashboard data.</p>
            <button onClick={() => window.location.reload()} className="btn-primary">
              Refresh Page
            </button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white/90">Admin Dashboard</h1>
          <p className="text-white/50 mt-1 text-sm">Welcome back! Here's what's happening today.</p>
        </div>
        <div className="flex items-center space-x-2">
          <Link to="/analytics" className="btn-secondary text-sm flex items-center space-x-2">
            <BarChart3 className="w-4 h-4" />
            <span>View Analytics</span>
          </Link>
        </div>
      </div>

      {/* Main Stats Grid - 6 columns on large screens */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard
          title="Total Users"
          value={userStats?.total_users || 0}
          icon={<Users />}
          color="teal"
          trend={userStats?.new_users_this_week && userStats.new_users_this_week > 0 ? "up" : "neutral"}
          trendValue={userStats?.new_users_this_week ? `+${userStats.new_users_this_week}` : undefined}
        />
        <StatCard
          title="Students"
          value={userStats?.total_students || 0}
          icon={<GraduationCap />}
          color="teal"
        />
        <StatCard
          title="Teachers"
          value={userStats?.total_teachers || 0}
          icon={<BookOpen />}
          color="green"
        />
        <StatCard
          title="Total Exams"
          value={examStats?.total_exams || 0}
          icon={<FileText />}
          color="purple"
        />
        <StatCard
          title="Active Exams"
          value={examStats?.active_exams || 0}
          icon={<Activity />}
          color="orange"
        />
        <StatCard
          title="Pass Rate"
          value={`${(examStats?.average_pass_rate || 0).toFixed(0)}%`}
          icon={<TrendingUp />}
          color="blue"
        />
      </div>

      {/* Secondary Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="stat-card stat-card-blue">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-white/50 uppercase tracking-wider">Scheduled</p>
              <p className="text-xl font-bold text-white/90 mt-1">{examStats?.scheduled_exams || 0}</p>
            </div>
            <Calendar className="w-8 h-8 text-blue-400/60" />
          </div>
        </div>
        <div className="stat-card stat-card-emerald">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-white/50 uppercase tracking-wider">Completed</p>
              <p className="text-xl font-bold text-white/90 mt-1">{examStats?.completed_exams || 0}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-emerald-400/60" />
          </div>
        </div>
        <div className="stat-card stat-card-amber">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-white/50 uppercase tracking-wider">Today's Exams</p>
              <p className="text-xl font-bold text-white/90 mt-1">{examStats?.exams_today || 0}</p>
            </div>
            <Clock className="w-8 h-8 text-amber-400/60" />
          </div>
        </div>
        <div className="stat-card stat-card-purple">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-white/50 uppercase tracking-wider">Active Users</p>
              <p className="text-xl font-bold text-white/90 mt-1">{userStats?.active_users || 0}</p>
            </div>
            <ShieldCheck className="w-8 h-8 text-purple-400/60" />
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity - Takes 2 columns */}
        <Card className="lg:col-span-2">
          <CardHeader
            title="Recent Activity"
            subtitle="Latest system activity"
            icon={<Activity className="w-4 h-4 text-teal-400" />}
          />
          {recentActivity.length > 0 ? (
            <div className="space-y-2">
              {recentActivity.slice(0, 6).map((activity: any) => (
                <div
                  key={activity.id}
                  className="flex items-start space-x-3 p-3 rounded-lg bg-white/[0.02] border border-white/[0.04] hover:border-white/[0.08] transition-colors"
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    activity.type === 'user_created' ? 'bg-emerald-500/20' :
                    activity.type === 'exam_created' ? 'bg-blue-500/20' :
                    activity.type === 'exam_completed' ? 'bg-purple-500/20' :
                    'bg-white/5'
                  }`}>
                    {activity.type === 'user_created' ? <UserPlus className="w-4 h-4 text-emerald-400" /> :
                     activity.type === 'exam_created' ? <FileText className="w-4 h-4 text-blue-400" /> :
                     activity.type === 'exam_completed' ? <CheckCircle className="w-4 h-4 text-purple-400" /> :
                     <Activity className="w-4 h-4 text-white/40" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white/80 truncate">{activity.description}</p>
                    <p className="text-xs text-white/40 mt-0.5">
                      {activity.user_name} • {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Activity className="w-10 h-10 text-white/20 mx-auto mb-2" />
              <p className="text-white/40 text-sm">No recent activity</p>
            </div>
          )}
        </Card>

        {/* User Distribution */}
        <Card>
          <CardHeader
            title="User Distribution"
            subtitle="By role"
            icon={<Users className="w-4 h-4 text-teal-400" />}
          />
          <UserDistribution userStats={userStats} />
          <CardFooter className="flex items-center justify-between">
            <div className="text-xs text-white/40">
              {userStats?.inactive_users || 0} inactive users
            </div>
            <Link to="/admin/users" className="text-xs text-teal-400 hover:text-teal-300 flex items-center space-x-1">
              <span>View all</span>
              <ArrowRight className="w-3 h-3" />
            </Link>
          </CardFooter>
        </Card>
      </div>

      {/* Exam Status & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Exam Status Overview */}
        <Card>
          <CardHeader
            title="Exam Status"
            subtitle="Distribution by status"
            icon={<FileText className="w-4 h-4 text-teal-400" />}
          />
          <ExamStatusDistribution examStats={examStats} />
          <div className="mt-4 pt-4 border-t border-white/[0.06]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-white/40">Average Score</p>
                <p className="text-lg font-semibold text-white/90">{(examStats?.average_score || 0).toFixed(1)}%</p>
              </div>
              <div>
                <p className="text-xs text-white/40">Total Attempts</p>
                <p className="text-lg font-semibold text-white/90">{examStats?.total_attempts || 0}</p>
              </div>
              <CircularProgress
                value={examStats?.average_pass_rate || 0}
                size={48}
              />
            </div>
          </div>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader
            title="Quick Actions"
            subtitle="Common administrative tasks"
            icon={<ShieldCheck className="w-4 h-4 text-teal-400" />}
          />
          <div className="grid grid-cols-1 gap-3">
            <Link
              to="/admin/users"
              className="flex items-center space-x-3 p-3 rounded-lg bg-white/[0.02] border border-white/[0.04] hover:border-teal-500/30 hover:bg-teal-500/5 transition-all group"
            >
              <div className="w-9 h-9 bg-teal-500/20 rounded-lg flex items-center justify-center border border-teal-500/30">
                <Users className="w-4 h-4 text-teal-400" />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-medium text-white/90">Manage Users</h4>
                <p className="text-xs text-white/40">Add, edit, or remove users</p>
              </div>
              <ArrowRight className="w-4 h-4 text-white/20 group-hover:text-teal-400 transition-colors" />
            </Link>

            <Link
              to="/admin/tenants"
              className="flex items-center space-x-3 p-3 rounded-lg bg-white/[0.02] border border-white/[0.04] hover:border-emerald-500/30 hover:bg-emerald-500/5 transition-all group"
            >
              <div className="w-9 h-9 bg-emerald-500/20 rounded-lg flex items-center justify-center border border-emerald-500/30">
                <FileText className="w-4 h-4 text-emerald-400" />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-medium text-white/90">Manage Tenants</h4>
                <p className="text-xs text-white/40">Configure organizations</p>
              </div>
              <ArrowRight className="w-4 h-4 text-white/20 group-hover:text-emerald-400 transition-colors" />
            </Link>

            <Link
              to="/analytics"
              className="flex items-center space-x-3 p-3 rounded-lg bg-white/[0.02] border border-white/[0.04] hover:border-purple-500/30 hover:bg-purple-500/5 transition-all group"
            >
              <div className="w-9 h-9 bg-purple-500/20 rounded-lg flex items-center justify-center border border-purple-500/30">
                <TrendingUp className="w-4 h-4 text-purple-400" />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-medium text-white/90">View Analytics</h4>
                <p className="text-xs text-white/40">System-wide reports</p>
              </div>
              <ArrowRight className="w-4 h-4 text-white/20 group-hover:text-purple-400 transition-colors" />
            </Link>
          </div>
        </Card>
      </div>

      {/* Growth Metrics */}
      <Card>
        <CardHeader
          title="Growth Metrics"
          subtitle="User growth over time"
          icon={<TrendingUp className="w-4 h-4 text-teal-400" />}
        />
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-4 rounded-lg bg-gradient-to-br from-teal-500/10 to-transparent border border-teal-500/10">
            <p className="text-xs text-teal-400 font-medium mb-1">New Today</p>
            <p className="text-2xl font-bold text-white/90">{userStats?.new_users_today || 0}</p>
          </div>
          <div className="text-center p-4 rounded-lg bg-gradient-to-br from-emerald-500/10 to-transparent border border-emerald-500/10">
            <p className="text-xs text-emerald-400 font-medium mb-1">This Week</p>
            <p className="text-2xl font-bold text-white/90">{userStats?.new_users_this_week || 0}</p>
          </div>
          <div className="text-center p-4 rounded-lg bg-gradient-to-br from-purple-500/10 to-transparent border border-purple-500/10">
            <p className="text-xs text-purple-400 font-medium mb-1">This Month</p>
            <p className="text-2xl font-bold text-white/90">{userStats?.new_users_this_month || 0}</p>
          </div>
        </div>
      </Card>
    </div>
  );
};
